import { StandardProps } from "../componentMap";
import { YourekoUI } from "./YourekoUI";
import { useVariant } from "../../hooks/useVariant/useVariant";
import { dispatchAnalyticsEvent } from "../../utils";
import { useEffect, useRef } from "react";
import { getOwnBrand } from "../../utils/product/getOwnBrand";
import { sanitiseTitle } from "../Title/sanitiseTitle";
import { getDefaultCategory } from "../../utils/product/getDefaultCategory";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";

const eventsToCapture = ["views"];

const getAnalyticsPayload = (
  brandName: string | undefined,
  category: string | undefined,
  skuId: string,
  price: string | undefined,
  productName: string,
  productId: string,
) => ({
  type: "pdp",
  subType: "yourEko - view modal",
  additionalInfo: {
    soPost: "true",
  },
  properties: {},
  products: [
    {
      brand: brandName,
      category: category,
      sku: skuId,
      price: price,
      productName: productName,
      productId: productId,
    },
  ],
});

const eligibleProductTypes = [
  "Freezers",
  "Fridge Freezers",
  "Fridges",
  "Washing Machines",
  "Washer Dryers",
  "Tumble Dryers",
  "Dishwashers",
];

const YourEkoEvents = {
  YOUREKO_CTA_INTERACTION: "YOUREKO_CTA_INTERACTION",
};

export function Youreko({
  product,
  flags,
}: Pick<StandardProps, "product" | "flags">) {
  const {
    brand,
    aliases: { pimProductId },
    sellingProductType,
  } = product;

  const {
    variant: {
      aliases: { skuId },
      price,
      title,
    },
  } = useVariant(product);

  const showYourEKO = flags?.showYourEKO?.value === true;

  const ownBrand = getOwnBrand(product);
  const sanitisedTitle = sanitiseTitle(title, ownBrand);
  const defaultCategory = getDefaultCategory(product);
  const capturedEvents = useRef<Array<string>>([]);
  const isExcludedProductType =
    !sellingProductType || !eligibleProductTypes.includes(sellingProductType);

  useEffect(() => {
    if (isExcludedProductType || !showYourEKO) return;

    capturedEvents.current = [];
    window.raiseYourekoEvent = (eventName) => {
      if (
        eventsToCapture.includes(eventName) &&
        !capturedEvents.current.includes(eventName)
      ) {
        capturedEvents.current.push(eventName);

        dispatchAnalyticsEvent(
          "global.interaction",
          getAnalyticsPayload(
            brand?.name,
            defaultCategory,
            skuId,
            price?.value,
            sanitisedTitle,
            pimProductId,
          ),
          {},
        );

        dispatchMonetateQEvent(YourEkoEvents.YOUREKO_CTA_INTERACTION);
      }
    };

    return () => {
      window.raiseYourekoEvent = undefined;
    };
  }, [skuId]);

  if (isExcludedProductType || !showYourEKO) return null;

  return <YourekoUI />;
}
